/**
 * A CÉGEK NYILVÁNTARTÁSÁNAK ÁLLAPOTA — a Beállítások lap első szelete (#25).
 *
 * ⚠️ EZ A LISTA AZ INAKTÍV CÉGEKET IS HOZZA, és ez a `GET /companies` és a
 * munka cégsora közti EGYETLEN lényegi különbség. Ha innen is kiesnének, az
 * inaktiválás EGYIRÁNYÚ lenne: nem volna felület, amin vissza lehet
 * kapcsolni. A szűrés a felületen történik — a „Inaktívak is" kapcsolóval —,
 * és ez az a RITKA eset, ahol a felületi szűrés helyes: nem jogosultságot
 * szűkít, hanem nézetet.
 *
 * ═══ MIÉRT NEM `useReducer`, PEDIG SOK AZ ÁLLAPOT ═══
 *
 * Mert a repó másik két horga (`useNavCredentials`, `useNavSync`) is így néz
 * ki, és egy harmadik minta itt nem nyer semmit. Az olvasó, aki megértette a
 * kulcsbevitel horgát, ezt is érti — a `fail()` ág, az `alive` őr és az
 * `inFlight` zár ugyanaz a három fogás, ugyanazokkal az okokkal.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/features/auth/AuthProvider';
import { ApiError, api } from '@/lib/api-client';

/**
 * A CÉGENKÉNT KAPCSOLHATÓ BIZONYLATTÍPUSOK (#25/3) — a `DocType` enum
 * részhalmaza. A `BANK_STATEMENT` NINCS köztük: e-mailben jön a banktól, nem
 * az ügyfél tölti fel, tehát egy kapcsoló rá semmit nem változtatna.
 */
export type CompanyDocType = 'INBOUND_INVOICE' | 'OUTBOUND_INVOICE' | 'RECEIPT' | 'OTHER';

/** Egy sor a nyilvántartásban — a backend `CompanyAdminRow`-ja. */
export interface CompanyRow {
  id: string;
  name: string;
  taxNumber: string;
  groupMemberTaxNumber: string | null;
  retentionYears: number;
  chaseGraceDays: number;
  fiscalYearStartMonth: number | null;
  locale: string;
  /** A KK vállalkozáskódja (#73), vagy `null`, ha nem ismerjük. */
  tensoftCode: string | null;
  active: boolean;
  navTechUserConfigured: boolean;
  /** ISO-szöveg, ahogy a JSON hozza. */
  createdAt: string;
  /** Amit ennél a cégnél az ügyfél megadhat a „Mi ez?" kérdésre (#25/3). */
  docTypes: CompanyDocType[];
}

/**
 * AMIT AZ ŰRLAP KÜLD. A `null` HÁROM MEZŐN MŰVELET, nem hiányzó adat:
 * a csoport adószámánál „a csoportos adóalanyiság megszűnt", a könyvelési év
 * kezdeténél „naptári év", a Tensoft-kódnál „nem ismerjük". A többi mezőn a `null` HIBA — a szerver 400-zal
 * utasítja el (`update-company.dto.ts`).
 */
export interface CompanyInput {
  name: string;
  taxNumber: string;
  groupMemberTaxNumber: string | null;
  retentionYears: number;
  chaseGraceDays: number;
  fiscalYearStartMonth: number | null;
  locale: string;
  tensoftCode: string | null;
}

export type CompaniesStatus =
  | 'loading'
  | 'ready'
  | 'loadFailed'
  | 'forbidden'
  | 'saving'
  | 'saved'
  | 'failed'
  | 'invalid'
  | 'conflict';

export interface CompaniesState {
  status: CompaniesStatus;
  companies: CompanyRow[];
  /** A szerver üzenete a 400-as és a 409-es ághoz — azt mutatjuk, nem sajátot. */
  detail: string;
  create: (input: CompanyInput, onSaved: () => void) => void;
  update: (companyId: string, input: CompanyInput, onSaved: () => void) => void;
  setActive: (companyId: string, active: boolean) => void;
  /**
   * A cég TELJES típuslistája, nem egy kapcsolás — a szerver is így veszi
   * (`PUT /companies/:id/doc-types`). Két párhuzamos kattintás így nem tud a
   * sorrenden múlni.
   */
  setDocTypes: (companyId: string, docTypes: CompanyDocType[], onSaved?: () => void) => void;
  /**
   * A „Mégse" hívja. Az állapot MEGOSZTOTT a lista és az űrlap között: nélküle
   * az elvetett űrlap 409-e a listán állna tovább, mintha a lista hibája lenne.
   */
  clearWriteError: () => void;
  reload: () => void;
}

/** Az írás hibaágai — ezeket viszi el a `clearWriteError`. A `forbidden` NEM: az a lapé. */
const WRITE_ERRORS: ReadonlySet<CompaniesStatus> = new Set(['failed', 'invalid', 'conflict']);

export function useCompanies(): CompaniesState {
  const { signOut } = useAuth();
  const [status, setStatus] = useState<CompaniesStatus>('loading');
  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [detail, setDetail] = useState('');

  /**
   * ⚠️ A ZÁR REFERENCIÁN ÜL, NEM ÁLLAPOTON — ugyanaz a fogás, mint a
   * `useNavCredentials`-ben. Egy `setStatus(current => …)` frissítőjében
   * indított kérés `StrictMode` alatt KÉTSZER futna le; itt a második egy
   * MÁSODIK cégfelvitel lenne, ugyanazzal az adószámmal — és a szerver
   * 409-cel válaszolna rá, vagyis a felhasználó a SAJÁT első mentésétől
   * kapna „már létezik" hibát.
   */
  const inFlight = useRef(false);

  /** A lecsatolt lap nem ír állapotot. */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const fail = useCallback(
    (caught: unknown, onError: (next: CompaniesStatus, message: string) => void) => {
      const httpStatus = caught instanceof ApiError ? caught.status : 0;
      const message = caught instanceof ApiError ? caught.message : '';

      if (httpStatus === 401) {
        // Nem a művelet hibája: a munkamenet járt le.
        signOut('expired');
        return;
      }
      if (httpStatus === 403) {
        onError('forbidden', '');
        return;
      }
      if (httpStatus === 400) {
        // A szerver üzenete magyarul jön (a DTO üzenetei), és pontosabb,
        // mint amit itt írni tudnánk: megmondja, MELYIK mező alakja rossz.
        onError('invalid', message);
        return;
      }
      if (httpStatus === 409) {
        // ⚠️ KÜLÖN ÁG, ÉS EZ NEM SZÉPÉSZET. Az ütköző adószám üzenete
        // MEGNEVEZI a céget, és megmondja, ha az INAKTÍV — vagyis a teendő
        // nem új felvitel, hanem visszakapcsolás. Egy általános „nem
        // sikerült" ezt elnyelné, és a könyvelő újra meg újra próbálkozna.
        onError('conflict', message);
        return;
      }
      onError('failed', message);
    },
    [signOut]
  );

  /**
   * ⚠️ CSAK A LEGUTÓBBI BETÖLTÉS ÍRHAT. Két gyors mentés két újratöltést indít,
   * és a válaszok sorrendje nem garantált: a KÉSVE érkező régebbi lista
   * különben felülírná az újabbat, és a képernyő egy már megváltozott állapotot
   * mutatna — frissítésig senki nem venné észre.
   */
  const latestLoad = useRef(0);

  const reload = useCallback(() => {
    latestLoad.current += 1;
    const load = latestLoad.current;
    setStatus('loading');

    void api<CompanyRow[]>('/companies')
      .then((list) => {
        if (!alive.current || load !== latestLoad.current) {
          return;
        }
        setCompanies(list);
        setStatus('ready');
      })
      .catch((caught: unknown) => {
        if (!alive.current || load !== latestLoad.current) {
          return;
        }
        fail(caught, (next) => setStatus(next === 'forbidden' ? 'forbidden' : 'loadFailed'));
      });
  }, [fail]);

  // Nyitáskor egyszer — és nem `useEffect(reload, [reload])` alakban: azt a
  // Biome szükségtelen függőségnek olvassa, a `lint-gate` pedig a
  // figyelmeztetést is hibának veszi.
  useEffect(() => {
    reload();
  }, [reload]);

  /**
   * A HÁROM ÍRÁSI ÚT KÖZÖS TÖRZSE.
   *
   * ⚠️ A SIKER UTÁN ÚJRATÖLTÜNK, nem a helyi tömböt írjuk át. A szerver
   * SZÁMOL is: a `PATCH` az adószámot normalizálatlanul tárolja, a
   * `navTechUserConfigured` pedig a kredencia-táblából jön — egy helyi
   * összefésülés ezekben csendben elcsúszna a valóságtól. Nyolcvan sor
   * újratöltése egy adminisztrációs képernyőn nem ár.
   *
   * ⚠️ AZ ŰRLAP AZ `onSaved`-RE ZÁRUL, NEM A `saved` ÁLLAPOTRA. A `saved` és
   * az újratöltés `loading`-ja egy renderelésbe esik, az űrlap a `saved`-et
   * soha nem látja — a mentés lement, az űrlap nyitva maradt. A `useUsers`
   * ugyanezt a fogást használja, ugyanezért.
   */
  const write = useCallback(
    (path: string, method: 'POST' | 'PATCH' | 'PUT', body: unknown, onSaved?: () => void) => {
      if (inFlight.current) {
        return;
      }
      inFlight.current = true;
      setStatus('saving');
      setDetail('');

      void api<CompanyRow>(path, { method, body: JSON.stringify(body) })
        .then(() => {
          if (!alive.current) {
            return;
          }
          onSaved?.();
          setStatus('saved');
          reload();
        })
        .catch((caught: unknown) => {
          if (!alive.current) {
            return;
          }
          fail(caught, (next, message) => {
            setStatus(next);
            setDetail(message);
          });
        })
        .finally(() => {
          inFlight.current = false;
        });
    },
    [fail, reload]
  );

  const create = useCallback(
    (input: CompanyInput, onSaved: () => void) => write('/companies', 'POST', input, onSaved),
    [write]
  );

  const update = useCallback(
    (companyId: string, input: CompanyInput, onSaved: () => void) =>
      write(`/companies/${companyId}`, 'PATCH', input, onSaved),
    [write]
  );

  /**
   * ⚠️ KÜLÖN ÚTVONAL, NEM A `PATCH` RÉSZE — a szerver is így osztja
   * (`company.controller.ts`). Egy DÖNTÉS nem eshet bele egy űrlap
   * mentésébe: máskülönben egy régóta nyitva felejtett lap elavult adattal
   * visszaküldve kikapcsolhatna egy céget, amit közben valaki visszakapcsolt.
   */
  const setActive = useCallback(
    (companyId: string, active: boolean) =>
      write(`/companies/${companyId}/active`, 'PUT', { active }),
    [write]
  );

  /**
   * A TÍPUS-ENGEDÉLYEK ÍRÁSA KIVÉTEL A KÖZÖS `write` ALÓL, két ponton — és
   * mindkettő a mátrix fő mozdulatából következik (code review, 2026-09-13):
   * a kolléga egy oszlopot pásztáz végig lefelé, cégről cégre.
   *
   * 1. A ZÁR CÉGENKÉNTI, nem globális. A közös `inFlight` azért van, hogy egy
   *    második `POST /companies` ne vigyen fel két céget ugyanazzal az
   *    adószámmal; KÉT KÜLÖNBÖZŐ cég engedélye között ilyen veszély nincs.
   *    Globális zárral a következő kattintás gépelési sebességnél CSENDBEN
   *    elveszne — a pipa nem mozdulna, és kérés sem menne el.
   * 2. NEM TÖLT ÚJRA, hanem a VÁLASZ SORÁT írja a listába. A szerver a friss
   *    sort adja vissza; egy teljes újratöltés minden kattintásnál nyolcvan
   *    sort kérne le, és a közben érkező régebbi lista vissza is írhatná a
   *    már megváltozott pipákat.
   *
   * UGYANARRA A CÉGRE a második kattintás kimarad, amíg az első válasz meg nem
   * jön: a törzs TELJES állapotot ír, tehát két egyidejű kérés az uniójukban
   * is kiköthet (`company-admin.service.ts`).
   */
  const docTypeWrites = useRef(new Set<string>());

  const setDocTypes = useCallback(
    (companyId: string, docTypes: CompanyDocType[], onSaved?: () => void) => {
      if (docTypeWrites.current.has(companyId)) {
        return;
      }
      docTypeWrites.current.add(companyId);
      setStatus('saving');
      setDetail('');

      void api<CompanyRow>(`/companies/${companyId}/doc-types`, {
        method: 'PUT',
        body: JSON.stringify({ docTypes }),
      })
        .then((row) => {
          if (!alive.current) {
            return;
          }
          setCompanies((current) =>
            current.map((company) => (company.id === row.id ? row : company))
          );
          onSaved?.();
        })
        .catch((caught: unknown) => {
          if (!alive.current) {
            return;
          }
          fail(caught, (next, message) => {
            setStatus(next);
            setDetail(message);
          });
        })
        .finally(() => {
          docTypeWrites.current.delete(companyId);
          // A LÁB CSAK AKKOR NYUGSZIK EL, ha MINDEN írás lement: egy gyors
          // pásztázásnál különben az első válasz eltüntetné a „Mentés…"-t,
          // miközben a többi még úton van.
          if (alive.current && docTypeWrites.current.size === 0) {
            setStatus((current) => (current === 'saving' ? 'saved' : current));
          }
        });
    },
    [fail]
  );

  const clearWriteError = useCallback(() => {
    setStatus((current) => (WRITE_ERRORS.has(current) ? 'ready' : current));
    setDetail('');
  }, []);

  return {
    status,
    companies,
    detail,
    create,
    update,
    setActive,
    setDocTypes,
    clearWriteError,
    reload,
  };
}
